import type { Path } from 'react-hook-form';
import type { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import type { Translator } from '@/types/translator.type';
import {
  buildPastedRoomPartitions,
  type PastedRoomTariff,
  type RoomOptions,
} from '@/utils/benefits/hotel/excelPaste/parseRoomTariffBlock';
import {
  isCompositionAllowed,
  ROOM_COMPOSITIONS,
  ROOM_TYPE_KEYS,
} from '@/utils/benefits/hotel/excelPaste/roomCompositions';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import type { ExcelPasteRecapRow } from './ExcelPastePanel';
import {
  buildPastedFormPeriods,
  formatRecapAmount,
  FormPeriod,
  getPeriodFieldNames,
} from './pasteFormHelpers';
import {
  createDefaultPartition,
  Partition,
  RoomOptionsForm,
  Tariff,
} from './updateCategoryRoomOptions';

const setOptions = { shouldDirty: true, shouldValidate: true };

/** "Total chambre" lines of the recap, current price against pasted price. */
export function buildRoomAmountsRecap(
  current: Tariff | undefined,
  tickedOptions: RoomOptions,
  pasted: PastedRoomTariff,
  childBandsCount: ChildBandsCount,
  formatComposition: (row: number) => string,
): ExcelPasteRecapRow[] {
  return ROOM_COMPOSITIONS.filter((composition) =>
    isCompositionAllowed(composition, childBandsCount),
  ).map((composition) => {
    const position = tickedOptions[composition.roomType].indexOf(
      composition.optionIndex,
    );
    const currentPrice =
      position >= 0
        ? current?.pricePartitions?.[composition.roomType]?.[position]?.price
        : undefined;
    return {
      key: `composition-${composition.row}`,
      label: formatComposition(composition.row),
      current: formatRecapAmount(currentPrice),
      pasted: formatRecapAmount(pasted.amounts[composition.row - 1]),
    };
  });
}

/** Notes on the compositions a paste ticks and unticks in the category. */
export function buildRoomOptionsNotes(
  {
    tickedRows,
    untickedRows,
  }: { tickedRows: number[]; untickedRows: number[] },
  childBandsCount: ChildBandsCount,
  formatComposition: (row: number) => string,
  t: Translator,
): string[] {
  // A room type losing (or gaining) all its compositions is named once
  // ("Quadruples (all compositions)") rather than listed line by line.
  const listCompositions = (rows: number[]) =>
    ROOM_TYPE_KEYS.flatMap((roomType) => {
      const typeRows = rows.filter(
        (row) => ROOM_COMPOSITIONS[row - 1]?.roomType === roomType,
      );
      if (typeRows.length === 0) return [];
      const offered = ROOM_COMPOSITIONS.filter(
        (composition) =>
          composition.roomType === roomType &&
          isCompositionAllowed(composition, childBandsCount),
      ).length;
      return typeRows.length === offered && offered > 1
        ? [
            t('hotels.pasteFromExcel.notes.wholeType', {
              type: t(`hotels.roomCategory.roomTypes.${roomType}`),
            }),
          ]
        : typeRows.map(formatComposition);
    }).join(', ');

  return [
    ...(tickedRows.length > 0
      ? [
          t('hotels.pasteFromExcel.notes.ticked', {
            compositions: listCompositions(tickedRows),
            interpolation: { escapeValue: false },
          }),
        ]
      : []),
    ...(untickedRows.length > 0
      ? [
          t('hotels.pasteFromExcel.notes.unticked', {
            compositions: listCompositions(untickedRows),
            interpolation: { escapeValue: false },
          }),
        ]
      : []),
  ];
}

/**
 * Writes a pasted tariff (label, periods, "Total chambre") into the form,
 * once the category options are `nextOptions`. Returns the field names to
 * highlight.
 */
export function writePastedRoomTariff(
  { getValues, setValue }: RoomOptionsForm,
  tariffPath: string,
  pasted: PastedRoomTariff,
  nextOptions: RoomOptions,
): string[] {
  const set = (name: string, value: unknown) =>
    setValue(
      name as Path<HotelFormDataWithTranslations>,
      value as never,
      setOptions,
    );

  const tariff = getValues(
    tariffPath as `hotelRoomCategories.${number}.tarifs.${number}`,
  ) as Tariff;
  const priceFieldNames: string[] = [];

  ROOM_TYPE_KEYS.forEach((roomType) => {
    if (nextOptions[roomType].length === 0) return;
    const path = `${tariffPath}.pricePartitions.${roomType}`;
    set(
      path,
      buildPastedRoomPartitions(
        roomType,
        nextOptions[roomType],
        (tariff.pricePartitions?.[roomType] ?? []) as Partition[],
        pasted.amounts,
        createDefaultPartition(tariff),
      ),
    );
    nextOptions[roomType].forEach((_, position) =>
      priceFieldNames.push(`${path}.${position}.price`),
    );
  });

  set(`${tariffPath}.name`, pasted.name);
  set(
    `${tariffPath}.periods`,
    buildPastedFormPeriods(
      tariff.periods as FormPeriod[] | undefined,
      pasted.periods,
    ),
  );

  return [
    `${tariffPath}.name`,
    ...getPeriodFieldNames(`${tariffPath}.periods`, pasted.periods.length),
    ...priceFieldNames,
  ];
}
